import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';
import { recreateAppBlockScripts } from '@theme/morph';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // The slide the shopper is currently viewing. A variant change (e.g.
    // choosing another size) re-renders the whole gallery in place; without
    // this it would always snap back to the first slide.
    //
    // Restore by the slide's stable `slide-id`, not by raw index: the prepended
    // special slides — exploded mount viewer, interactive 3D viewer, the four
    // room mockups — and every real media slide now carry one. A4 / S omits the
    // exploded-mount slide (it ships with a desktop stand, not the leaf+magnet
    // system), so it has five prepended slides instead of six; a raw index would
    // land one slide off when switching between A4 and a larger size. The index
    // is kept only as a fallback (e.g. the exploded slide's own id is absent
    // from an A4 gallery — then index 0, the 3D viewer, is the right landing).
    const previousIndex = this.slideshow?.current ?? 0;
    const previousSlideId = this.slideshow?.slides?.[previousIndex]?.getAttribute('slide-id') || null;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);

        // `newMediaGallery` was built by DOMParser and inserted via
        // replaceWith — browsers never execute <script> tags that arrive that
        // way (same rule morph.js works around for a normal section morph).
        // The exploded-mount viewer's self-loading module script is the
        // concrete case this fixes: it only exists in the gallery for Size
        // M/L/XL, so the first time a shopper switches to one of those sizes
        // client-side, its script tag needs this same revival or the custom
        // element never upgrades and the viewer stays blank for the rest of
        // the page's life. See recreateAppBlockScripts' doc comment.
        recreateAppBlockScripts(newMediaGallery);

        if (previousSlideId || previousIndex > 0) {
          this.#restoreSlide(newMediaGallery, previousSlideId, previousIndex);
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Re-selects the slide that was open before the in-place re-render in
   * `#handleProductSelect`, addressed by stable `slide-id` (falling back to a
   * numeric index).
   *
   * The freshly inserted `<slideshow-component>` runs its OWN one-time initial
   * selection asynchronously (`assets/slideshow.js` `#setupSlideshow`, deferred
   * via `scheduler.schedule` + `requestAnimationFrame`) — which can land well
   * over a second later when the destination gallery mounts the heavy WebGL
   * exploded viewer on slide 0, and unconditionally re-selects slide 0 for
   * itself if nothing is visible yet. That internal selection is a genuine
   * race against the immediate `select()` call below: it fires *after*, and
   * silently reverts the restore straight back to slide 0 — confirmed by
   * tracing the actual `slideshow:select` events fired: ours lands correctly,
   * then a second, non-user-initiated one immediately follows, moving back to
   * index 0. (An earlier version of this fix tried to out-wait that race with
   * a `requestAnimationFrame` retry loop; that polling approach turned out to
   * be unreliable, since animation frames aren't guaranteed to keep firing
   * promptly on every viewport as a page settles the same way `select()`'s own
   * internal scroll timing isn't.)
   *
   * The robust fix doesn't try to out-wait that internal call — it reacts to
   * it directly: every `slideshow:select` the fresh gallery dispatches is
   * inspected, and any non-user-initiated one that lands somewhere other than
   * the target slide (i.e. that internal reset, whenever it actually fires)
   * is immediately corrected with another `select()`. That correction is
   * itself non-user-initiated and already on the target index, so it can't
   * re-trigger itself. The listener is removed — via `interaction` — the
   * instant the shopper actually navigates the gallery themselves (a
   * user-initiated select), so this can never fight a shopper's own click.
   * It otherwise just goes quiet on its own once this `gallery` is replaced
   * by a further size change, since nothing dispatches events on it anymore.
   *
   * @param {Element} gallery - The newly inserted `<media-gallery>`.
   * @param {string | null} slideId - Preferred: the `slide-id` to restore to.
   * @param {number} fallbackIndex - Used when `slideId` is null or absent from the new gallery.
   */
  #restoreSlide(gallery, slideId, fallbackIndex) {
    const slideshow = /** @type {MediaGallery} */ (gallery).slideshow;
    const slides = slideshow?.slides;
    if (!slideshow || !slides?.length) return;

    const matched = slideId ? slides.find((slide) => slide.getAttribute('slide-id') === slideId) : null;
    const targetIndex = matched ? slides.indexOf(matched) : Math.min(fallbackIndex, slides.length - 1);

    // Nothing to restore: the wanted slide is (or, being absent, falls back
    // to) slide 0 — the fresh slideshow already starts there.
    if (targetIndex <= 0) return;

    const interaction = new AbortController();
    gallery.addEventListener(
      'slideshow:select',
      (selectEvent) => {
        const detail = /** @type {any} */ (selectEvent).detail;
        if (detail?.userInitiated) {
          interaction.abort();
          return;
        }
        if (detail?.index !== targetIndex) slideshow.select(targetIndex, undefined, { animate: false });
      },
      { signal: interaction.signal }
    );

    slideshow.select(targetIndex, undefined, { animate: false });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
